import React, { useMemo } from 'react';
import Helmet from 'react-helmet';
import { Link } from 'react-router-dom';
import { DefaultTooltip, Button, Row, Col, LoadingSpinner, Switch } from 'twyne-new.ui';

import { debounce } from 'lodash';
import Moment from 'moment-timezone'

import history from '@Utils/history';
import ConfirmationPopover from 'components/ConfirmationPopover';
import { useAppAlert } from '@Components/AppAlert';
import appStore from 'stores/app'

import Table from '../RemoteTable';

import Bulk from './Bulk';
import { ChevronsUpDown, ChevronUp, ChevronDown, Pencil, Trash, Copy } from 'lucide-react';
import InfiniteScroll from '@/components/Grid/components/Table/InfiniteScroll';
// import Search from './DefaultSearch';
// import SidebarFilters from './filters/Filters'

export default class DefaultGrid extends React.Component {
  // OVERRITE ATTRIBUTES
  // entityName = string
  // entityNamePlural = string
  // entityNameModule = string
  // store = BaseStore
  // columns = array
  // extraActions = [func]
  // hasActiveField = bool
  tableProps = {}
  formatters = {
    iconSort: (col, _, opts) => <SortedColumn data={col} order={opts.sortElement.props.order} />,
    progressBar: (cell, row) => {
      if (row.sendToday === null || cell === null || cell === undefined) return '';
      const { success, total } = cell;
      const percent = Math.round(success / total * 100);
      return (
        <>
          <span className='text-dark'>{`${success} Delivered`} <span className='text-secondary'>{` / ${total} Assigned`}</span></span>
          <DefaultTooltip content={success === 0 ? <span>No integrations sent.</span> : <><div>{percent}%</div> </>}>
            <div style={{ maxWidth: '80%' }}>
              <div className="progress" style={{ height: '4px' }}>
                <div
                  className='progress-bar text-secondary'
                  role="progressbar"
                  style={{ width: `${isNaN(percent) ? 0 : percent}%`, backgroundColor: "rgb(169, 232, 249)" }}
                  aria-valuenow={isNaN(percent) ? 0 : percent}
                  aria-valuemin="0"
                  aria-valuemax="100"
                />
              </div>
            </div>
          </DefaultTooltip>
        </>
      )
    },

    active: (_, row) => <>
      <Switch value={row.active} onChange={this.handleUpdateActive(row)} />
    </>,

    actions: (_, row) => <div>
      <DefaultTooltip content='Edit' side='left'>
        <Link to={`${this.basePath || `/${this.entityNameModule}/${this.entityNamePlural.toLowerCase()}`}/${row.id}`}>
          <Pencil size={16} />
        </Link>
      </DefaultTooltip>
      {this.renderDeleteAction(row)}
      {this.extraActions && this.extraActions.map(action => action(row))}
    </div>,

    duplicateAction: row => {
      const id = `duplicate-${row.id}`
      return <>
        <ConfirmationPopover
          id={id}
          title={<span>Duplicate {this.entityName.toLowerCase()} <i>{`[#${row.id}] ${row.name || row.description}`}</i></span>}
          target={<DefaultTooltip content='Duplicate' side='left'><Copy size={16} /></DefaultTooltip>}
        >
          <p><strong>Are you sure you want duplicate this entry?</strong></p>
          <Button color="danger" onClick={this.handleDuplicateEntry(row)} block>Yes, I am sure</Button>
        </ConfirmationPopover>,
      </>
    },
  }

  constructor(props) {
    super(props)
    this.state = {
      filters: {},
      showSidebarFilters: true,
      term: '',
      options: {
        page: 1,
        sizePerPage: 25,
        sortField: 'id',
        sortOrder: 'asc',
      },
    }
  }

  loadData = async () => {
    this.store.list(this.query).then(() => {
      this.setState({})
    });
    this.setState({})
  }

  get query() {
    const { filters, term, options } = this.state

    const search = [term]

    for (const [key, value] of Object.entries(filters)) {
      search.push(`${key}=${JSON.stringify(value)}`);
    }

    return { options, term: search.join(' '), ...this.params }
  }

  get params() {
    return {}
  }

  get loadData() {
    return this._loadData
  }

  set loadData(fn) {
    this._loadData = debounce(fn, 1000, { leading: true })
  }

  componentWillMount() {
    this.store.resetCollection();
    this.loadData();
  }

  componentWillReceiveProps() {
    this.loadData();
  }

  componentWillUnmount() {
    this.store.resetCollection();
  }

  getDefaultFiltersSidebar() {
    const filters = {}
    this.filtersSidebar && this.filtersSidebar.map(filter => {
      const filterHasOverride = this.overrideDefaultValue && this.overrideDefaultValue.some(item => item === filter.name)
      if (filter.defaultValue !== undefined && !filterHasOverride) {
        filters[filter.name] = filter.defaultValue
      }
    })

    return filters
  }

  useParams = () => {
    const defaultFilters = this.getDefaultFiltersSidebar()
    const { filters, term, options } = this.state
    const { pathname } = history.location
    const filtersMerged = { ...defaultFilters, ...filters }

    return useMemo(() => {
      pathname; // keep to force load on route change

      const search = [term]

      for (const [key, value] of Object.entries(filtersMerged)) {
        search.push(`${key}=${JSON.stringify(value)}`);
      }

      return {
        ...this.params,
        options,
        term: search.join(' '),
      }
    }, [term, filters, options, pathname])
  }

  useNewStore = useStore => {
    const { collections: { useLoad, items, reset, list, actives, hasNextPage, total, showing } = {}, collection: { save, deleteEntry, fetchById } = {} } = useStore()

    const params = this.useParams()

    const loadData = useLoad(params)

    this.store = useMemo(() => ({
      hasNextPage: hasNextPage,
      showing: showing,
      actives: actives,
      resetCollection: reset,
      collection: {
        count: total,
        objects: items,
      },
      fetch: async id => {
        const data = await fetchById(id)
        return { data }
      },
      list,
      save: async data => {
        this.setState({}, async () => {
          await save(data)
          reset()
          await loadData()
        })
      },
      updateField: async (id, field, value, extraFields = {}) => {
        await save({
          id,
          [field]: value,
          ...extraFields,
        })
        await loadData()
        this.setState({})
      },
      delete: deleteEntry,
      loadData,
    }), [hasNextPage, showing, actives, reset, total, items, fetchById, deleteEntry, save, loadData, list])
  }

  useNewAlert = () => {
    const { handleSuccess, handleError } = useAppAlert()
    this.alert = useMemo(() => ({
      handleSuccess, handleError,
    }), [handleSuccess, handleError])
  }

  handleChangeFilter = change => {
    const { filters } = this.state
    change(filters)
    this.setState({ filters: { ...filters } }, this.loadData)
  }

  handleChangeFilters = async (key, option) => {
    this.handleChangeFilter(filters => {
      this.overrideDefaultValue = [...this.overrideDefaultValue || [], key]
      if ((Array.isArray(option) && option.length === 0) || option === null) {
        delete filters[key]
      } else {
        filters[key] = option
      }
    })
  }

  applyFilter = (key, value, color) => {
    this.handleChangeFilter(filters => {
      const insertIdx = filters.findIndex(f => f.key === key)
      const deleteCount = insertIdx === -1 ? 0 : 1

      const newItem = { key, value, color }

      filters.splice(insertIdx, deleteCount, newItem)
    })
  }

  removeFilter = idx => {
    this.handleChangeFilter(filters => {
      filters.splice(idx, 1)
    })
  }

  clearAllFilters = () => {
    this.overrideDefaultValue = this.filtersSidebar.map(item => item.name)
    const filters = {}

    Object.entries(this.filtersSidebar).forEach(([_, value]) => {
      if (value.type === "date-range-picker") {
        filters[value.name] = {
          $gte: Moment.tz(appStore.timezone).startOf('day'),
          $lte: Moment.tz(appStore.timezone).endOf('day'),
        }
      }
    })

    this.setState({ filters }, this.loadData)
  }

  handleTableChange = (_, { sortField, sortOrder, ...__ }) => {
    this.setState(({ options }) => ({
      options: {
        ...options,
        sortField,
        sortOrder,
      },
    }), this.loadData);
  }

  handleNextPage = () => {
    this.setState(({ options: { sizePerPage, ...options } }) => ({
      options: {
        ...options,
        sizePerPage: sizePerPage + sizePerPage,
      },
    }), this.loadData)
  }

  handleSearch = term => {
    this.setState({ term }, this.loadData)
  }

  handleAction = async (message, action) => {
    const { handleSuccess, handleError } = this.alert
    try {
      await action()
      handleSuccess('Success', `${this.entityName} ${message}.`);
    } catch (error) {
      handleError('Error', `${this.entityName} was not ${message}.`);
    }
  }

  handleDelete = row => async () => {
    await this.handleAction('deleted', async () => {
      await this.store.resetCollection();
      await this.store.delete(row, true)
    })
    this.setState(({ options }) => ({
      options: {
        ...options,
        page: 1,
      },
    }), this.loadData)
  }

  handleUpdateActive = row => async () => {
    await this.handleAction('edited', async () => {
      await this.store.updateField(row.id, 'active', !row.active)
    })
    await this.loadData();
  }

  handleDuplicateEntry = row => async () => {
    await this.handleAction('duplicated', async () => {
      row.id = null
      await this.store.save(row)
    })
    await this.loadData();
  }

  renderDeleteAction = row => {
    const id = `delete-${row.id}`
    return <>
      <ConfirmationPopover
        title={<span>Delete {this.entityName.toLowerCase()} <i>{`[#${row.id}] ${row.name || row.description}`}</i></span>}
        id={id}
        target={<DefaultTooltip content='Delete' side='left'><Trash size={16} /></DefaultTooltip>}
      >
        <p><strong>Are you sure you want do this?</strong></p>
        <Button color="danger" onClick={this.handleDelete(row)} block>Yes, I am sure</Button>
      </ConfirmationPopover>
    </>
  }

  renderTable = () => <Table columns={this.columns} data={this.store.collection.objects} onTableChange={this.handleTableChange} bsProps={this.tableProps} />

  renderScroll() {
    return <div className="flex w-full justify-center items-center h-14">
      {this.store.hasNextPage && <InfiniteScroll onVisit={this.handleNextPage} hasMore={this.store.hasNextPage} />}
      {this.store.isLoading && <LoadingSpinner />}
    </div>
  }

  Filters = () => {
    const { filters } = this.state

    return <Row className='align-items-center m-0'>
      {filters && filters.length ? <Col sm='auto' className='p-0'>
        <Row className='m-0'>
          {filters.map(({ key, value, color = 'primary' }, idx) => <Col className='pl-0 pr-1' key={idx}>
            <div className='flex items-center gap-2'>
              <Button color={color} size='sm' className='badge text-lowercase border-0'>{key}:{`${value}`}</Button>
              <Button color={color} size='sm' className='badge text-lowercase border-0' onClick={() => this.removeFilter(idx)}>X</Button>
            </div>
          </Col>)}
        </Row>
      </Col> : null}
    </Row>
  }

  renderSearch = () => <>
    {/* <Search handleSearch={this.handleSearch}
      count={this.store.collection.count}
      showing={this.store.showing}
      actives={this.hasActiveField && this.store.actives}
      entityName={this.entityName}
      entityNamePlural={this.entityNamePlural}
    /> */}
  </>

  HeaderButtons = () => <>
    <Link to={`${this.basePath || `/${this.entityNameModule}/${this.entityNamePlural.toLowerCase()}`}/add`} className="btn btn-outline-success">Add {this.entityName}</Link>
  </>

  renderHeaderButtons = () => <div className='mb-3'>
    <div className="float-right">
      <this.HeaderButtons />
    </div>
    <this.Filters />
  </div>

  renderFiltersSideBar = () => {
    const { showSidebarFilters } = this.state

    if (showSidebarFilters && this.filtersSidebar) {
      // return <SidebarFilters
      //   overrideDefaultValue={this.overrideDefaultValue}
      //   definition={this.filtersSidebar}
      //   data={this.state.filters}
      //   filterName={this.entityNamePlural}
      //   clearAllFilters={this.clearAllFilters}
      // />
    }
  }

  Content = () => this.renderContent()

  renderContent = () => {
    this.useNewAlert()
    return <div className="padding-content-heading">
      <Helmet title={this.entityNamePlural} />
      {this.renderFiltersSideBar()}
      {this.renderSearch()}
      {this.renderHeaderButtons()}
      {this.renderTable()}
      {this.renderScroll()}
    </div>
  }

  render() {
    return <this.Content />
  }
}

export function SortedColumn({ data: { text, sort }, order }) {
  let Icon = ChevronsUpDown
  switch (order) {
    case 'desc':
      Icon += ChevronUp
      break;
    case 'asc':
      Icon += ChevronDown
      break;
  }
  return <>
    {text} {sort && <Icon size='10' />}
  </>
}

export { Bulk }
